import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useAuthStore } from './authStore';
import odooClient from '../services/odooClient';

// Helper function to check if an artwork status is restricted from cart
// Only "In stock" items can be added to cart, all other statuses are restricted
const isRestrictedStatus = (status: string): boolean => {
  const allowedStatuses = ['In stock', 'in stock', 'In Stock','in transit',"In transit","in Transit"]; // Add other allowed statuses as needed];
  return !allowedStatuses.includes(status);
};

export interface CartItem {
  id: string; // artwork id (string)
  IdName?: string;
  title: string;
  artist: string;
  prices?: Record<string, number>;
  price: number;
  currency: string;
  image_url?: string;
  status?: string;
}

interface CartState {
  // Persist all users
  userIdToItems: Record<string, CartItem[]>;
  // Button states for better UX
  removingItems: Set<string>; // Track items being removed
  // Sync helpers
  syncFromServer: () => Promise<void>;
  getItems: () => CartItem[];
  getCount: () => number;
  isInCart: (artworkId: string) => boolean;
  addToCart: (item: CartItem) => Promise<void>;
  removeFromCart: (artworkId: string) => Promise<void>;
  clearCart: () => Promise<void>;
}

const getCurrentUserId = (): string | null => {
  const user = useAuthStore.getState().user;
  return user?.id || null;
};

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      userIdToItems: {},
      removingItems: new Set(),

      // Load server cart into local store for current user
      syncFromServer: async () => {
        const uid = getCurrentUserId();
        if (!uid) return;
        try {
          const serverItems = await odooClient.getMyCart();
          // Normalize price to number
          // const normalized = (serverItems as CartItem[]).map(it => ({
          //   ...it,
          //   price: typeof it.price === 'number' ? it.price : Number(it.price || 0),
          //   prices: it.prices ?? localItem?.prices,
          // }));
          const normalized = (serverItems as CartItem[]).map(it => {
            const localItem = get().getItems().find(i => i.id === it.id);

            return {
              ...it,
              price: typeof it.price === 'number' ? it.price : Number(it.price || 0),
              prices: it.prices ?? localItem?.prices, // 🔥 PRESERVE prices
            };
          });
          set(state => ({ userIdToItems: { ...state.userIdToItems, [uid]: normalized } }));
        } catch (error) {
          console.error('Failed to sync cart from server:', error);
        }
      },

      getItems: () => {
        const uid = getCurrentUserId();
        if (!uid) return [];
        return get().userIdToItems[uid] || [];
      },

      getCount: () => {
        return get().getItems().length;
      },

      isInCart: (artworkId: string) => {
        return get().getItems().some(item => item.id === artworkId);
      },

      addToCart: async (item: CartItem) => {
        const uid = getCurrentUserId();
        if (!uid) return; // Require auth

        // Check if item is restricted from being added to cart
        if (item.status && isRestrictedStatus(item.status)) {
          console.warn(`Cannot add item with status "${item.status}" to cart. Only "In stock" items are allowed.`);
          return; // Don't add restricted items
        }

        // Optimistic local add
        set(state => {
          const current = state.userIdToItems[uid] || [];
          if (current.some(i => i.id === item.id)) return state;
          return { userIdToItems: { ...state.userIdToItems, [uid]: [...current, item] } };
        });
        // Sync with server and reconcile
        try {
          const serverItems = await odooClient.addCartItem(item.id);
          // if (Array.isArray(serverItems)) {
          //   const normalized = (serverItems as CartItem[]).map(it => ({
          //     ...it,
          //     price: typeof it.price === 'number' ? it.price : Number(it.price || 0)
          //   }));
          //   set(state => ({ userIdToItems: { ...state.userIdToItems, [uid]: normalized } }));
          // }
          if (Array.isArray(serverItems)) {
            const normalized = (serverItems as CartItem[]).map(it => {
              const localItem = get()
                .userIdToItems[uid]
                ?.find(i => i.id === it.id);

              return {
                ...it,
                price: typeof it.price === 'number'
                  ? it.price
                  : Number(it.price || 0),

                prices: it.prices ?? localItem?.prices, // 🔥 FIX
              };
            });

            set(state => ({
              userIdToItems: { ...state.userIdToItems, [uid]: normalized }
            }));
          }

        } catch (_) {
          // keep optimistic state on failure
        }
      },

      removeFromCart: async (artworkId: string) => {
        const uid = getCurrentUserId();
        if (!uid) return;

        // Check if already removing this item
        if (get().removingItems.has(artworkId)) {
          console.log(`Item ${artworkId} is already being removed`);
          return;
        }

        // Mark as removing
        set(state => ({
          removingItems: new Set([...state.removingItems, artworkId])
        }));

        // Optimistic local remove - immediate UI update
        set(state => {
          const current = state.userIdToItems[uid] || [];
          const filtered = current.filter(item => item.id !== artworkId);
          return { userIdToItems: { ...state.userIdToItems, [uid]: filtered } };
        });

        // Sync with server in background
        try {
          const serverItems = await odooClient.removeCartItem(artworkId);
          // if (Array.isArray(serverItems)) {
          //   const normalized = (serverItems as CartItem[]).map(it => ({
          //     ...it,
          //     price: typeof it.price === 'number' ? it.price : Number(it.price || 0)
          //   }));
          //   // Update with server data and clear removing state
          //   set(state => ({
          //     userIdToItems: { ...state.userIdToItems, [uid]: normalized },
          //     removingItems: new Set([...state.removingItems].filter(id => id !== artworkId))
          //   }));
          // } 
          if (Array.isArray(serverItems)) {
            const normalized = (serverItems as CartItem[]).map(it => {
              const localItem = get()
                .userIdToItems[uid]
                ?.find(i => i.id === it.id);

              return {
                ...it,
                price: typeof it.price === 'number'
                  ? it.price
                  : Number(it.price || 0),

                prices: it.prices ?? localItem?.prices, // 🔥 IMPORTANT
              };
            });

            set(state => ({
              userIdToItems: { ...state.userIdToItems, [uid]: normalized },
              removingItems: new Set([...state.removingItems].filter(id => id !== artworkId))
            }));
          }

          else {
            // Remove from removing items if server didn't return valid data
            set(state => ({
              removingItems: new Set([...state.removingItems].filter(id => id !== artworkId))
            }));
          }
        } catch (error) {
          console.error('Failed to remove item from cart:', error);
          // Remove from removing items on error
          set(state => ({
            removingItems: new Set([...state.removingItems].filter(id => id !== artworkId))
          }));
        }
      },

      clearCart: async () => {
        const uid = getCurrentUserId();
        if (!uid) return;
        try {
          const ok = await odooClient.clearCartItems();
          if (ok) {
            set(state => ({ userIdToItems: { ...state.userIdToItems, [uid]: [] } }));
            return;
          }
        } catch (_) { }
        // Fallback local clear
        set(state => ({ userIdToItems: { ...state.userIdToItems, [uid]: [] } }));
      }
    }),
    {
      name: 'cart-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ userIdToItems: state.userIdToItems })
    }
  )
);


